import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
	faChartLine,
	faCircleCheck,
	faCircleXmark,
	faLock,
} from "@fortawesome/pro-regular-svg-icons";

import { Modal } from "@/components/Modals/Modal";
import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import { CampaignLockLeadStates } from "../utils/campaignLockLeadStates";

/** A numbered section heading, matching the two-step layout of the explainer. */
const SectionHeading: FunctionComponent<{ index: number; children: ReactNode }> = ({
	index,
	children,
}) => (
	<div className="flex items-center gap-2 mb-3">
		<span className="w-5 h-5 shrink-0 rounded-[5px] bg-text text-white text-[11px] font-bold flex items-center justify-center">
			{index}
		</span>
		<h3 className="text-[13.5px] font-bold">{children}</h3>
	</div>
);

/** One "you can" / "you can't" line in the restrictions section. */
const Rule: FunctionComponent<{ allowed: boolean; title: string; text: string }> = ({
	allowed,
	title,
	text,
}) => (
	<div className="flex items-start gap-2.5">
		<Icon
			className={classNames(
				"w-4 h-4 shrink-0 mt-0.5",
				allowed ? "text-status-success" : "text-status-error"
			)}
			icon={allowed ? faCircleCheck : faCircleXmark}
		/>
		<span>
			<span className="block text-[13px] font-semibold leading-snug">{title}</span>
			<span className="block text-xs text-text-meta leading-relaxed mt-0.5">{text}</span>
		</span>
	</div>
);

/**
 * The three buckets a locked campaign's leads fall into, in sequence order. `safe` drives the
 * verdict's colour: the two safe buckets can be moved to a duplicate, the running one cannot.
 */
const LEAD_STATE_ROWS = [
	{ key: "scheduled", dotClass: "bg-status-success", safe: true },
	{ key: "running", dotClass: "bg-status-warning", safe: false },
	{ key: "ended", dotClass: "bg-text-disabled", safe: true },
] as const;

/**
 * The full explanation of a campaign's structural lock (PREC-3786): what the lock blocks, what it
 * still allows, and how to get an editable copy without misreading the numbers afterwards.
 *
 * The lead-state counts come from the campaign-stats the list already loads — see
 * {@link resolveCampaignLockLeadStates}. The analytics warning is not decoration: campaign stats
 * group by the lead's *current* campaign, so moving a lead moves its contacted/reply/sentiment
 * history out of this campaign and into the copy.
 */
export const CampaignLockModal: FunctionComponent<{
	isOpened: boolean;
	campaignName: string;
	leadStates: CampaignLockLeadStates;
	isDuplicating?: boolean;
	onClose: () => void;
	onDuplicate: () => void;
}> = ({ isOpened, campaignName, leadStates, isDuplicating = false, onClose, onDuplicate }) => {
	const { t } = useTranslation();

	return (
		<Modal
			handleClose={onClose}
			icon={
				<span className="w-9 h-9 rounded-[9px] bg-status-warning-light text-status-warning flex items-center justify-center">
					<Icon className="w-[18px] h-[18px]" icon={faLock} />
				</span>
			}
			isOpened={isOpened}
			size="sm"
			subtitle={t("campaignLockExplainer.subtitle")}
			title={t("campaignLockExplainer.title")}
			titleWeight="bold"
			footer={
				<>
					<span className="text-xs text-text-meta pr-4">
						{t("campaignLockExplainer.footerHint")}
					</span>
					<div className="flex items-center gap-2 shrink-0">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.S}
							title={t("campaignLockExplainer.close")}
							type="button"
							onClick={onClose}
						/>
						<Button
							color={ButtonColor.ACTION}
							isLoading={isDuplicating}
							size={ButtonSize.S}
							title={t("campaignLockExplainer.duplicate")}
							type="button"
							onClick={onDuplicate}
						/>
					</div>
				</>
			}
		>
			<div className="flex flex-col gap-5">
				<section data-testid="campaign-lock-restrictions">
					<SectionHeading index={1}>
						{t("campaignLockExplainer.restrictionsHeading")}
					</SectionHeading>
					<div className="flex flex-col gap-3 ml-7">
						<Rule
							allowed={false}
							text={t("campaignLockExplainer.rules.structure.text")}
							title={t("campaignLockExplainer.rules.structure.title")}
						/>
						<Rule
							allowed={false}
							text={t("campaignLockExplainer.rules.senders.text")}
							title={t("campaignLockExplainer.rules.senders.title")}
						/>
						<Rule
							allowed
							text={t("campaignLockExplainer.rules.messageText.text")}
							title={t("campaignLockExplainer.rules.messageText.title")}
						/>
					</div>
				</section>

				<section className="border-t border-border-light pt-5" data-testid="campaign-lock-howto">
					<SectionHeading index={2}>{t("campaignLockExplainer.howToHeading")}</SectionHeading>
					<p className="ml-7 text-[13px] text-text-meta leading-relaxed">
						{t("campaignLockExplainer.howToText")}
					</p>

					<div className="ml-7 mt-3 border border-border-light rounded-[10px] overflow-hidden">
						{LEAD_STATE_ROWS.map(({ key, dotClass, safe }, index) => (
							<div
								key={key}
								className={classNames(
									"grid grid-cols-[186px_1fr] gap-3.5 p-3 items-start",
									index > 0 && "border-t border-border-light"
								)}
								data-testid={`campaign-lock-lead-state-${key}`}
							>
								<div className="min-w-0">
									<div className="flex items-center gap-2">
										<span className={classNames("w-2 h-2 shrink-0 rounded-full", dotClass)} />
										<span className="text-[13px] font-semibold">
											{t(`campaignLockExplainer.leadStates.${key}.name`)}
										</span>
									</div>
									<p className="text-xs text-text-meta mt-1 pl-4">
										{t(`campaignLockExplainer.leadStates.${key}.count`, {
											count: leadStates[key],
										})}
									</p>
								</div>
								<p className="text-xs text-text-meta leading-relaxed">
									<span
										className={classNames(
											"block font-semibold mb-0.5",
											safe ? "text-status-success" : "text-status-warning"
										)}
									>
										{t(`campaignLockExplainer.leadStates.${key}.verdict`)}
									</span>
									{t(`campaignLockExplainer.leadStates.${key}.text`)}
								</p>
							</div>
						))}
					</div>

					<div
						className="ml-7 mt-3 flex gap-2.5 bg-status-warning-light/40 border border-status-warning-light rounded-[9px] px-3 py-2.5"
						data-testid="campaign-lock-analytics-warning"
					>
						<Icon className="w-4 h-4 shrink-0 mt-0.5 text-status-warning" icon={faChartLine} />
						<p className="text-xs leading-relaxed">
							<span className="block font-bold mb-0.5">
								{t("campaignLockExplainer.analyticsWarningTitle")}
							</span>
							{t("campaignLockExplainer.analyticsWarning", { name: campaignName })}
						</p>
					</div>
				</section>
			</div>
		</Modal>
	);
};
